import { Text, TruncatedContent } from "@aurem/ui";
import { useUserData } from "graphql/user";
import { useNavigation } from "hooks/navigate";
import { useI18n } from "providers/I18nProvider";
import { useCallback } from "react";
import { formatRelativeTime } from "utils/dates";
import { useNotificationContext } from "../context";
import type { Notification, NotificationData } from "../types";
import { getNotificationUrl } from "../utils";
import { NotificationCorrectionsList } from "./NotificationCorrectionsList";
import { NotificationItemIcon } from "./NotificationItemIcon";

type CorrectionItem = {
  attribute: string;
  oldValue: string;
  newValue: string;
};

type Props = {
  notification: Notification;
};

export const NotificationItem = ({ notification }: Props) => {
  const { language } = useI18n();
  const navigate = useNavigation();
  const { markAsRead, setIsOpen } = useNotificationContext();
  const userData = useUserData();
  const userRoles = userData?.user?.userRoles ?? [];

  const data = notification.data as NotificationData;
  const title = data.title[language] || "";
  const body = data.body ? data.body[language] || "" : "";
  const corrections = data.meta?.corrections as CorrectionItem[] | undefined;

  const isUnread = !notification.readAt;
  const notificationUrl = getNotificationUrl(data, language, userRoles);
  const handleClick = useCallback(async () => {
    if (isUnread) {
      void markAsRead(notification.id);
    }
    if (notificationUrl) {
      navigate(notificationUrl, { replace: false });
      setIsOpen(false);
    }
  }, [
    isUnread,
    markAsRead,
    notification.id,
    data.actionUrl,
    navigate,
    setIsOpen,
  ]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-start gap-3 rounded-xl bg-white py-5 ps-4 pe-5 text-start transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 group"
    >
      <NotificationItemIcon
        type={notification.type}
        category={data.category}
        isUnread={isUnread}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TruncatedContent>
          <Text
            as="span"
            size="sm"
            weight="medium"
            className="text-neutral-500"
          >
            {title}
          </Text>
        </TruncatedContent>

        {body && (
          <Text size="sm" className="text-neutral-500">
            {body}
          </Text>
        )}

        {corrections && corrections.length > 0 && (
          <NotificationCorrectionsList corrections={corrections} />
        )}

        <Text size="xs" className="mt-1 text-secondary-200">
          {formatRelativeTime(notification.createdAt, language)}
        </Text>
      </div>
    </button>
  );
};
